import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

export interface Attachment {
  id: number
  tenant_id: string
  fk_id: number
  fk_table: string
  title?: string
  description?: string
  file_name: string
  file_size: number
  file_type: string
  storage_type: string
  uploaded_by: number
  created_at: string
  download_url?: string
}

interface AttachmentFilters {
  fk_table?: string
  fk_id?: number
}

interface UploadAttachmentParams {
  file: File
  fk_id?: number
  fk_table?: string
  title?: string
  description?: string
}

interface UpdateAttachmentParams {
  attachment_id: number
  title?: string
  description?: string
}

const FILES_BASE_URL = '/api/attachments'

export const useAttachments = (filters?: AttachmentFilters) => {
  return useQuery<Attachment[]>({
    queryKey: ['attachments', filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.fk_table) {
        params.append('fk_table', filters.fk_table)
      }
      if (filters?.fk_id) {
        params.append('fk_id', filters.fk_id.toString())
      }
      const url = params.toString() ? `${FILES_BASE_URL}?${params}` : FILES_BASE_URL
      const { data } = await apiClient.get<Attachment[]>(url)
      return data
    },
    enabled: true,
  })
}

export const useAttachment = (attachmentId: number) => {
  return useQuery<Attachment>({
    queryKey: ['attachment', attachmentId],
    queryFn: async () => {
      const { data } = await apiClient.get<Attachment>(`${FILES_BASE_URL}/${attachmentId}`)
      return data
    },
    enabled: !!attachmentId,
  })
}

export const useUploadAttachment = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: UploadAttachmentParams) => {
      const formData = new FormData()
      formData.append('file', params.file)
      if (params.fk_id) formData.append('fk_id', params.fk_id.toString())
      if (params.fk_table) formData.append('fk_table', params.fk_table)
      if (params.title) formData.append('title', params.title)
      if (params.description) formData.append('description', params.description)
      const { data } = await apiClient.post<Attachment>(FILES_BASE_URL, formData)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attachments'] })
    },
  })
}

export const useUpdateAttachment = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: UpdateAttachmentParams) => {
      const { attachment_id, ...updateData } = params
      const { data } = await apiClient.put<Attachment>(`${FILES_BASE_URL}/${attachment_id}`, updateData)
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['attachment', data.id] })
      qc.invalidateQueries({ queryKey: ['attachments'] })
    },
  })
}

export const useDeleteAttachment = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { attachment_id: number; fk_table: string; fk_id: number }) => {
      await apiClient.delete(`${FILES_BASE_URL}/${params.attachment_id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attachments'] })
    },
  })
}

export const useGetSignedUrl = (attachmentId: number) => {
  return useQuery<{ url: string; expires_in: number }>({
    queryKey: ['signedUrl', attachmentId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ url: string; expires_in: number }>(
        `${FILES_BASE_URL}/${attachmentId}/signed-url`
      )
      return data
    },
    enabled: !!attachmentId,
  })
}
